import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useUserRole } from './UserRoleContext'
import { AuthContext, type EventSummary } from './AuthContext'
import { PageRegistryContext } from './PageRegistryContext'
import { deriveEventFamily } from '@/lib/eventFamily'
import { extractClarificationReplies } from '@/lib/clarificationReplies'
import { usePageContext } from '@/hooks/usePageContext'
import type { ChatChart } from '@/types/chart'

export type MessageType = 'user' | 'bot' | 'system'
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'failed'

/**
 * V3 Page 07 — chat-created sidebar pins. The orchestrator emits two
 * optional frames atop the `done` envelope when CHAT_PIN_SIDEBAR=1 and
 * the turn classifies as an artifact-producing intent. Shape mirrors
 * apps/backend/src/chat/artifact-composer.js.
 */
export interface ArtifactPreviewFrame {
  kind: string
  spec_draft: Record<string, unknown>
  source_turn_id: string | null
  suggested_label: string
  suggested_group: string
}

export interface PageSuggestionFrame {
  prompt: string
  cta_label: string
  explicit_pin: boolean
  suggested_label: string
  suggested_group: string
}

export interface Message {
  id: string
  type: MessageType
  content: string
  timestamp: Date
  status?: MessageStatus
  isTyping?: boolean
  isStreaming?: boolean
  quickReplies?: string[]
  /**
   * V2d 2026-04-20 — structured chart payload rendered below the markdown
   * text inside the bubble. Present only when the backend tool emits one
   * (see chartFromToolResult). Additive; messages without a chart ignore
   * the field.
   */
  chart?: ChatChart
  /**
   * V3 Page 07 — draft ArtifactSpec preview + pin suggestion. Present
   * only when the backend orchestrator emits them on the `done` frame
   * (feature-flagged on CHAT_PIN_SIDEBAR). Additive; older clients
   * ignore the fields cleanly.
   */
  artifactPreview?: ArtifactPreviewFrame
  pageSuggestion?: PageSuggestionFrame
  metadata?: {
    escalated?: boolean
    handoffRequested?: boolean
    priority?: 'low' | 'medium' | 'high' | 'urgent'
    category?: string
  }
}

export interface QuickReply {
  id: string
  text: string
  category?: string
  action?: 'send' | 'escalate' | 'handoff'
}

interface ChatContextType {
  messages: Message[]
  isTyping: boolean
  isStreaming: boolean
  isConnected: boolean
  currentConversationId: string | null
  quickReplies: QuickReply[]
  isEscalated: boolean
  handoffPending: boolean
  pendingMessage: string | null
  sendMessage: (content: string) => void
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void
  setIsTyping: (typing: boolean) => void
  setIsConnected: (connected: boolean) => void
  setQuickReplies: (replies: QuickReply[]) => void
  setPendingMessage: (text: string) => void
  requestEscalation: (reason?: string) => void
  requestHandoff: (reason?: string) => void
  clearChat: () => void
  updateMessageStatus: (messageId: string, status: MessageStatus) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

// Backend API URL. In production the backend serves both the PWA and the
// JSON/WS API from the same origin (sean.magicmeade.cloud), so defaulting
// to `window.location.origin` gives us correct wss:// + hostname without
// any build-time config. In dev, apps/frontend/.env.local sets
// VITE_API_URL=http://localhost:3001 so the Vite dev server on :3000 can
// reach the backend directly instead of going through its proxy.
const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8080')

// Backend `/ws` and `/api/chat` reject sessionIds that aren't RFC-4122 UUIDs
// (server.js validates with `isUuid` from the `uuid` package and silently
// returns `{ type: 'error' }` on mismatch — no log line). The pre-2026-04-18
// `Math.random + Date.now` generator produced base36 strings that always
// failed the check, so every chat turn looked like a server crash to the
// PWA's error fallback. Use the platform UUID generator with a v4 fallback
// for the rare browser/jsdom environment without crypto.randomUUID().
function newChatSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // RFC-4122 v4 fallback. Uses crypto.getRandomValues when available so we
  // still pass server-side `isUuid` validation in older Safari builds.
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function useChat() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}

interface ChatProviderProps {
  children: React.ReactNode
}

export function ChatProvider({ children }: ChatProviderProps) {
  const pageContext = usePageContext()
  const pageContextRef = useRef(pageContext)
  useEffect(() => { pageContextRef.current = pageContext }, [pageContext])

  const [messages, setMessages] = useState<Message[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [currentConversationId, _setCurrentConversationId] = useState<string | null>(null)
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([])
  const [isEscalated, setIsEscalated] = useState(false)
  const [handoffPending, setHandoffPending] = useState(false)
  const [pendingMessage, setPendingMessageState] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const sessionIdRef = useRef<string>(newChatSessionId())
  const streamingMessageIdRef = useRef<string | null>(null)
  // V2.5-A 2026-04-20 — live mirror of `messages` so the async quick-
  // reply fetcher (fire-and-forget from WS `done` handler) can inspect
  // the current message list without capturing a stale closure.
  const messagesRef = useRef<Message[]>([])
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // V2.6.1 — live mirror of AuthContext.events so the async quick-reply
  // fetcher can look up the active event's name (and derive its family)
  // without re-renders capturing stale closures. Read AuthContext directly
  // (not the useAuth helper which throws) so unit tests that mount
  // ChatProvider in isolation gracefully degrade to no-family-filter.
  const auth = React.useContext(AuthContext)
  const authEvents = auth?.events
  const eventsRef = useRef<EventSummary[]>([])
  useEffect(() => {
    eventsRef.current = Array.isArray(authEvents) ? authEvents : []
  }, [authEvents])

  const { currentUser } = useUserRole()

  // Connect to WebSocket
  useEffect(() => {
    // Codex HIGH-A fix (2026-04-17): auth precedence is
    //   (1) HttpOnly butler_session cookie — auto-attached by the browser
    //       on same-origin WebSocket upgrades; the backend /ws handler
    //       reads it via readSessionCookie().
    //   (2) Sec-WebSocket-Protocol: butler-auth.v1, <jwt> — non-cookie
    //       fallback for sessions that still have a sessionStorage token
    //       (legacy Sean sessions, no Set-Cookie issued). Puts the JWT in
    //       a request header instead of the URL so access logs don't
    //       capture a live bearer credential.
    // The ?token=<jwt> query fallback was removed with this commit.
    const legacyJwt =
      (typeof window !== 'undefined' &&
        (window.sessionStorage?.getItem('butler_token') ||
          window.localStorage?.getItem('butler_token'))) ||
      ''
    const wsUrl = API_URL.replace('http', 'ws') + '/ws'

    let retryCount = 0
    const connect = () => {
      try {
        const ws = legacyJwt
          ? new WebSocket(wsUrl, ['butler-auth.v1', legacyJwt])
          : new WebSocket(wsUrl)

        ws.onopen = () => {
          console.log('WebSocket connected')
          retryCount = 0
          setIsConnected(true)
        }

        ws.onclose = () => {
          console.log('WebSocket disconnected')
          setIsConnected(false)
          const delay = Math.min(3000 * Math.pow(2, retryCount), 60000)
            + Math.random() * 1000
          retryCount++
          setTimeout(connect, delay)
        }

        ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          setIsConnected(false)
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            handleServerMessage(data)
          } catch (e) {
            console.error('Failed to parse message:', e)
          }
        }

        wsRef.current = ws
      } catch (error) {
        console.error('Failed to connect:', error)
        setIsConnected(false)
      }
    }

    connect()

    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [])

  // V3 Page 07 — bridge `page_registry_invalidated` WS frames to the
  // PageRegistry provider so every open tab re-pulls the sidebar after
  // a pin/unpin/reorder/rename elsewhere. Read the context directly (not
  // the throwing hook) so ChatProvider still mounts cleanly in tests /
  // unauthed trees where PageRegistryProvider is absent. Wrap in a ref
  // so handleServerMessage (empty-deps useCallback) doesn't capture a
  // stale provider value on remount.
  const pageRegistry = React.useContext(PageRegistryContext)
  const pageRegistryRef = useRef(pageRegistry)
  useEffect(() => {
    pageRegistryRef.current = pageRegistry
  }, [pageRegistry])

  const handleServerMessage = useCallback((data: any) => {
    if (data.type === 'connected') {
      setIsConnected(true)
      return
    }

    // V3 Page 07 — chat-pin cross-tab sync frame. Fired by
    // emitPageRegistryInvalidated on any write to /api/pages/*.
    if (data.type === 'page_registry_invalidated') {
      pageRegistryRef.current?.notifyInvalidated()
      return
    }

    // Streaming: first chunk creates the bot message
    if (data.type === 'chunk') {
      setIsTyping(false)
      setIsStreaming(true)

      if (!streamingMessageIdRef.current) {
        // Create new bot message for first chunk
        const id = Math.random().toString(36).substring(2) + Date.now().toString(36)
        streamingMessageIdRef.current = id
        setMessages(prev => [...prev, {
          id,
          type: 'bot',
          content: data.text,
          timestamp: new Date(),
          isStreaming: true,
        }])
      } else {
        // Append to existing streaming message
        setMessages(prev => prev.map(msg =>
          msg.id === streamingMessageIdRef.current
            ? { ...msg, content: msg.content + data.text }
            : msg
        ))
      }
      return
    }

    // Streaming: quality validator rewrote the message
    if (data.type === 'replace') {
      if (streamingMessageIdRef.current) {
        setMessages(prev => prev.map(msg =>
          msg.id === streamingMessageIdRef.current
            ? { ...msg, content: data.text, isStreaming: false }
            : msg
        ))
      }
      return
    }

    // Streaming: done event finalizes the message
    if (data.type === 'done') {
      setIsStreaming(false)

      if (streamingMessageIdRef.current) {
        // Finalize the streaming message
        setMessages(prev => prev.map(msg =>
          msg.id === streamingMessageIdRef.current
            ? {
                ...msg,
                isStreaming: false,
                // V2d: attach optional structured chart payload (dropped
                // here when absent — old clients keep the pre-V2d shape).
                ...(data.chart ? { chart: data.chart as ChatChart } : {}),
                // V3 Page 07: attach optional artifact preview + page
                // suggestion frames. Both come from the orchestrator's
                // composeArtifactFrames on the `done` event. Additive;
                // absent for turns that didn't classify as artifact-
                // producing OR when CHAT_PIN_SIDEBAR flag is off.
                ...(data.artifact_preview
                  ? { artifactPreview: data.artifact_preview as ArtifactPreviewFrame }
                  : {}),
                ...(data.page_suggestion
                  ? { pageSuggestion: data.page_suggestion as PageSuggestionFrame }
                  : {}),
                metadata: {
                  category: data.intake?.route,
                  escalated: data.escalation?.escalated,
                },
              }
            : msg
        ))
        streamingMessageIdRef.current = null
      }

      // Handle escalation
      if (data.escalation?.escalated) {
        setIsEscalated(true)
        const id = Math.random().toString(36).substring(2) + Date.now().toString(36)
        setMessages(prev => [...prev, {
          id,
          type: 'system',
          content: `Support ticket created: ${data.escalation.ticketId}. A team member will contact you shortly.`,
          timestamp: new Date(),
          metadata: { escalated: true, priority: 'high' },
        }])
      }

      // V2.6.1 — clarification-aware quick replies. If the bot just asked
      // a clarifying question with bolded options ("Do you want **total
      // gross** or **pacing**? say **realtime** or **settlement**"), the
      // gallery picks are nonsensical follow-ups. Extract candidate
      // answers from the bot text first; only fall through to the
      // gallery when extraction yields fewer than 2 candidates.
      const finalBotText =
        messagesRef.current[messagesRef.current.length - 1]?.content ?? ''
      const clarReplies = extractClarificationReplies(finalBotText)
      if (clarReplies.length >= 2) {
        setQuickReplies(
          clarReplies.map((c) => ({ ...c, category: 'clarification' })),
        )
        return
      }

      // Set quick replies based on intent — async fetch from rotating gallery
      // with fallback to per-route hardcoded list. fire-and-forget; the
      // handler is already `async`, but we don't await to avoid blocking
      // the WS message handler on gallery latency.
      fetchQuickRepliesFromGallery(data.intake?.route)
        .then((replies) => setQuickReplies(replies))
        .catch(() => {
          const fallback =
            FALLBACK_REPLIES_BY_ROUTE[data.intake?.route ?? ''] ??
            FALLBACK_REPLIES_BY_ROUTE.default
          setQuickReplies(fallback)
        })
      return
    }

    // Non-streaming: complete response
    if (data.type === 'response') {
      setIsTyping(false)
      setIsStreaming(false)
      streamingMessageIdRef.current = null

      // Add bot response
      addMessage({
        type: 'bot',
        content: data.message || "I'm here to help!",
        // V2d: attach the optional chart payload when the backend emits it.
        ...(data.chart ? { chart: data.chart as ChatChart } : {}),
        // V3 Page 07 — non-streaming path carries the same optional frames.
        ...(data.artifact_preview
          ? { artifactPreview: data.artifact_preview as ArtifactPreviewFrame }
          : {}),
        ...(data.page_suggestion
          ? { pageSuggestion: data.page_suggestion as PageSuggestionFrame }
          : {}),
        metadata: {
          category: data.intake?.route,
          escalated: data.escalation?.escalated
        }
      })

      // Handle escalation
      if (data.escalation?.escalated) {
        setIsEscalated(true)
        addMessage({
          type: 'system',
          content: `Support ticket created: ${data.escalation.ticketId}. A team member will contact you shortly.`,
          metadata: { escalated: true, priority: 'high' }
        })
      }

      // V2.6.1 — clarification-aware quick replies (mirror of the `done`
      // branch). When the bot's complete reply is a question with bolded
      // options or "say X or Y", surface those as quick replies instead
      // of pulling unrelated gallery questions.
      const responseText = typeof data.message === 'string' ? data.message : ''
      const clarReplies = extractClarificationReplies(responseText)
      if (clarReplies.length >= 2) {
        setQuickReplies(
          clarReplies.map((c) => ({ ...c, category: 'clarification' })),
        )
        return
      }

      // Set quick replies from rotating gallery (async, non-blocking).
      fetchQuickRepliesFromGallery(data.intake?.route)
        .then((replies) => setQuickReplies(replies))
        .catch(() => {
          const fallback =
            FALLBACK_REPLIES_BY_ROUTE[data.intake?.route ?? ''] ??
            FALLBACK_REPLIES_BY_ROUTE.default
          setQuickReplies(fallback)
        })
    }

    if (data.type === 'error') {
      setIsTyping(false)
      setIsStreaming(false)
      streamingMessageIdRef.current = null
      addMessage({
        type: 'system',
        content: 'Something went wrong. The Butler beta flow logged the request and needs a follow-up check.'
      })
    }
  }, [])

  // Butler Buddy quick replies are analytics follow-ups, not device/ops
  // prompts. Each label must map to a phase:v1 question in
  // suggested-questions.json so the agent can actually answer it.
  //
  // 2026-04-20 V2 chat-fault D fix: the prior hardcoded list showed the
  // SAME 3 suggestions after every magicmoney turn — Brian's complaint
  // "quick replies should be relevant to the previous question and not
  // just the same over and over". We now rotate from the public gallery
  // endpoint (/api/suggestions/rotate) which returns a random subset of
  // wire_ready + partial_wire questions on every call. A local seen-set
  // ring buffer (last 9 ids) biases FRESH picks client-side so the same
  // 3 don't land back-to-back even if the server happens to re-rotate.
  // The hardcoded lists remain the FALLBACK when the fetch fails
  // (offline / network blip / gallery-corrupt 500) so the quick-reply
  // strip never goes empty.
  const FALLBACK_REPLIES_BY_ROUTE: Record<string, QuickReply[]> = {
    magicmoney: [
      { id: 'fb-mm-1', text: 'Break it down by plan mix', category: 'analytics' },
      { id: 'fb-mm-2', text: 'Compare year over year', category: 'analytics' },
      { id: 'fb-mm-3', text: 'Show me the hourly trend', category: 'analytics' },
    ],
    reports: [
      { id: 'fb-rp-1', text: 'Save this as a report', category: 'analytics' },
      { id: 'fb-rp-2', text: 'Add a chart to the dashboard', category: 'analytics' },
      { id: 'fb-rp-3', text: 'Export to CSV', category: 'analytics' },
    ],
    default: [
      { id: 'fb-d-1', text: 'Top revenue events', category: 'analytics' },
      { id: 'fb-d-2', text: 'Kiosk orders per hour', category: 'analytics' },
      { id: 'fb-d-3', text: 'Repeat-play breakdown', category: 'analytics' },
    ],
  }

  const quickReplyRecentIdsRef = useRef<string[]>([])
  const QUICK_REPLY_RECENT_CAP = 9

  /**
   * V2.5-A 2026-04-20 — Very narrow user-message → tool-name keyword scan.
   *
   * The backend scorer treats tool_tags membership as the continuity
   * signal. Since the server doesn't emit the invoked tool name on the
   * WS `done` envelope (that's a deliberate non-change in this fork),
   * we derive a best-effort hint from the last few USER messages. High-
   * precision patterns only — a false positive just re-weights the
   * suggestion ordering, but a broad catch-all would flatten every
   * signal and defeat the continuity bonus.
   *
   * Patterns intentionally mirror a subset of the backend backfill
   * script (apps/backend/scripts/backfill-gallery-tags.js). Drift is
   * tolerated: a new tool landing without an FE pattern just means
   * the continuity bonus won't fire for that tool on the next turn —
   * the event-bonus signal keeps suggestions relevant.
   */
  const USER_TEXT_TOOL_HINTS: Array<{ pattern: RegExp; tool: string }> = [
    { pattern: /\b(yoy|year\s*over\s*year|vs\s*\d{4}|prior\s*year|last\s*year|compare.*\d{4})\b/i, tool: 'compare_to_prior_year' },
    { pattern: /\b(revenue|gross|net|refund|fee|sales|earnings|dollars?\s*per)\b/i, tool: 'get_revenue' },
    { pattern: /\b(peak\s*hour|by\s*hour|per\s*hour|hourly|throughput|orders?\s*per\s*(hour|minute))\b/i, tool: 'mm_top_hours' },
    { pattern: /\b(daily|per\s*day|day\s*of|weekday|weekend|weekly|opening\s*day|closing\s*day)\b/i, tool: 'mm_daily_summary' },
    { pattern: /\b(plan|package|unlimited|pay[-\s]?as[-\s]?you[-\s]?go|payg|bundle|tier|reload)\b/i, tool: 'plan_performance' },
    { pattern: /\b(breakage|unused|leftover\s*(credit|balance))\b/i, tool: 'mm_breakage_report' },
    { pattern: /\b(ride|game|attraction|himalaya|scrambler|giant\s*wheel|ferris)\b/i, tool: 'top_attractions' },
    { pattern: /\b(top\s*\d+|top[-\s]?grossing|most[-\s]?played|most\s*popular|busiest)\b/i, tool: 'top_attractions' },
  ]

  const extractRecentToolNames = (msgs: Message[], lookback = 3): string[] => {
    const found = new Set<string>()
    const userMsgs = msgs.filter((m) => m.type === 'user').slice(-lookback)
    for (const m of userMsgs) {
      const text = String(m.content || '')
      for (const { pattern, tool } of USER_TEXT_TOOL_HINTS) {
        if (pattern.test(text)) found.add(tool)
      }
    }
    return [...found]
  }

  /**
   * V2.5-A 2026-04-20 — In-memory flag that flips to `true` the first
   * time the contextual endpoint responds 200. If it responds 404 (the
   * backend's SUGGESTIONS_CONTEXTUAL flag is OFF) or a transient 5xx,
   * stay on /rotate for the rest of the session. Avoids pinning failure
   * to the first turn only.
   */
  const contextualEnabledRef = useRef<boolean | null>(null)

  /**
   * V2.6 Worker-C 2026-04-20 — sibling flag for the /synthesized path.
   * Semantics mirror contextualEnabledRef:
   *   - null = untested; try on the next turn.
   *   - false = 404/401/403 observed this session; skip forever.
   *   - true = 200 observed at least once; keep trying (a 5xx leaves the
   *     ref untouched so we retry next turn).
   */
  const synthesizedEnabledRef = useRef<boolean | null>(null)
  // V2.6.1 — abort in-flight quick-reply fetches when a new turn kicks off,
  // otherwise a slow synthesized call from turn N can land AFTER turn N+1's
  // replies and overwrite them. Sequence counter + AbortController per run.
  const quickReplyFetchRef = useRef<AbortController | null>(null)
  const quickReplySeqRef = useRef(0)

  const fetchQuickRepliesFromGallery = async (route?: string): Promise<QuickReply[]> => {
    quickReplyFetchRef.current?.abort()
    const controller = new AbortController()
    quickReplyFetchRef.current = controller
    const seq = ++quickReplySeqRef.current
    const isStale = () => seq !== quickReplySeqRef.current || controller.signal.aborted
    const fallback =
      FALLBACK_REPLIES_BY_ROUTE[route ?? ''] ?? FALLBACK_REPLIES_BY_ROUTE.default

    const sidebarEventId =
      typeof window !== 'undefined'
        ? Number(
            window.sessionStorage.getItem('butler-buddy:currentEventId') || '',
          ) || null
        : null

    // V2.6 Worker-C: try the SYNTHESIZED endpoint FIRST when the user
    // has an active event. Data-anchored questions ("Why did Sunday
    // peak at $208K?") read far more naturally than the generic
    // contextual gallery picks, so when they're available we want them.
    // A 404 (flag off) / 401 / 403 flips the session ref and we never
    // retry. A 5xx / network error falls through without flipping, so
    // a transient failure doesn't lose us synthesized for the session.
    // No sidebar event → skip synthesized (engine requires eventId).
    if (
      sidebarEventId != null
      && synthesizedEnabledRef.current !== false
    ) {
      try {
        const sRes = await fetch('/api/suggestions/synthesized', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ event_id: sidebarEventId, max: 5 }),
          signal: controller.signal,
        })
        if (isStale()) return fallback
        if (sRes.status === 404 || sRes.status === 401 || sRes.status === 403) {
          synthesizedEnabledRef.current = false
        } else if (sRes.ok) {
          synthesizedEnabledRef.current = true
          const sData = await sRes.json()
          if (Array.isArray(sData?.questions) && sData.questions.length > 0) {
            const chosen = sData.questions
              .filter(
                (q: any) => typeof q?.text === 'string' && typeof q?.id === 'string',
              )
              .slice(0, 3)
              .map((q: any) => ({
                id: String(q.id),
                text: String(q.text),
                category: String(q.category ?? 'analytics'),
              })) as QuickReply[]

            if (chosen.length > 0) {
              const nextRecent = [
                ...chosen.map((q) => q.id),
                ...quickReplyRecentIdsRef.current,
              ].slice(0, QUICK_REPLY_RECENT_CAP)
              quickReplyRecentIdsRef.current = nextRecent
              return chosen
            }
          }
        }
        // Non-ok, non-404 (e.g. 500/503): fall through without flipping.
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[quick-replies] synthesized fetch failed, trying contextual:', err)
      }
    }

    // V2.5-A: try the contextual endpoint NEXT. If it returns 404 we
    // infer the backend flag is off and flip `contextualEnabledRef` so
    // subsequent turns don't pay the round-trip. Any other failure
    // falls through to /rotate as before.
    if (contextualEnabledRef.current !== false) {
      try {
        const recentToolNames = extractRecentToolNames(messagesRef.current, 3)
        // 2026-04-24 Codex pass-3 HIGH: the contextual POST is the
        // primary path when SUGGESTIONS_CONTEXTUAL=1. Without
        // active_event_family the backend can't apply the cross-family
        // filter, so Cherry/NOS/etc. chips can surface on the active
        // Santa Barbara session. Derive client-side and include.
        const contextualActiveEvent =
          sidebarEventId != null
            ? eventsRef.current.find((e) => e.EventID === sidebarEventId)
            : null
        const contextualFamily = deriveEventFamily(contextualActiveEvent?.EventName)
        const cRes = await fetch('/api/suggestions/contextual', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            event_id: sidebarEventId,
            recent_tool_names: recentToolNames,
            count: 3,
            // Only send when we have a resolved event family — omit on
            // the 'any' no-active-event case so the backend's
            // validateContextualBody path keeps its null default.
            ...(contextualFamily && contextualFamily !== 'any'
              ? { active_event_family: contextualFamily }
              : {}),
          }),
          signal: controller.signal,
        })
        if (isStale()) return fallback
        if (cRes.status === 404 || cRes.status === 401 || cRes.status === 403) {
          // 404 = backend flag off. 401/403 = auth/tenant gate rejected
          // us (session expired, mid-flight logout, or flag-before-auth
          // order got reversed). Either way, contextual is unavailable
          // for THIS session — flip the ref so we don't keep hammering
          // the auth-gated endpoint on every subsequent turn. Codex
          // 2026-04-20 V2.5-A HIGH #2 defense-in-depth.
          contextualEnabledRef.current = false
        } else if (cRes.ok) {
          contextualEnabledRef.current = true
          const cData = await cRes.json()
          if (Array.isArray(cData?.questions) && cData.questions.length > 0) {
            const chosen = cData.questions
              .filter((q: any) => typeof q?.text === 'string' && typeof q?.id === 'string')
              .slice(0, 3)
              .map((q: any) => ({
                id: String(q.id),
                text: String(q.text),
                category: String(q.category ?? 'analytics'),
              })) as QuickReply[]

            if (chosen.length > 0) {
              // Feed the ring buffer so the NEXT /rotate turn (if the
              // flag is flipped off again) doesn't immediately serve
              // these same ids.
              const nextRecent = [
                ...chosen.map((q) => q.id),
                ...quickReplyRecentIdsRef.current,
              ].slice(0, QUICK_REPLY_RECENT_CAP)
              quickReplyRecentIdsRef.current = nextRecent
              return chosen
            }
          }
        }
        // Non-ok, non-404: fall through to /rotate below (without
        // flipping the ref — next turn may succeed).
      } catch (err) {
        // Network / parse error on contextual — fall through to /rotate.
        console.warn('[quick-replies] contextual fetch failed, trying rotate:', err)
      }
    }

    try {
      // V2.6.1 — derive the active event family client-side and send it
      // to /rotate so the public endpoint can drop family-mismatched
      // gallery rows. Without this, NOS-active sessions still see
      // family=cherry rows like "Cherry Festival 2026 net revenue" — the
      // exact bleed Brian's V2.6 live-smoke C1 caught.
      const activeEvent =
        sidebarEventId != null
          ? eventsRef.current.find((e) => e.EventID === sidebarEventId)
          : null
      const family = deriveEventFamily(activeEvent?.EventName)
      // 2026-04-24 hotfix: also send `event_family=other` when the active
      // event is a known Butler event that doesn't match one of the
      // canonical family patterns (e.g. Santa Barbara Fair & Expo). Without
      // this, the public /rotate endpoint received no family param and
      // skipped filtering entirely — Cherry Festival / NOS / Indio chips
      // leaked into rotation while a different event was selected (Brian's
      // 2026-04-24 verbatim "I don't know why it keeps saying the Cherry
      // Festival"). The 'any' family still means "no active event" so we
      // continue to skip the param in that case.
      const familyParam =
        family && family !== 'any'
          ? `&event_family=${encodeURIComponent(family)}`
          : ''

      // Pull a wider pool than we need (count=9) so client-side dedupe
      // against the recent-seen ring has room to pick fresh items. Keep
      // it <= MAX_COUNT (20) enforced by the router.
      const res = await fetch(`/api/suggestions/rotate?count=9${familyParam}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      })
      if (isStale()) return fallback
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!Array.isArray(data?.questions)) throw new Error('bad shape')

      const seen = new Set(quickReplyRecentIdsRef.current)
      const pool = data.questions
        .filter((q: any) => typeof q?.text === 'string' && typeof q?.id === 'string')
        .map((q: any) => ({
          id: String(q.id),
          text: String(q.text),
          category: String(q.category ?? 'analytics'),
        })) as QuickReply[]

      // Prefer ids the user hasn't seen in the last QUICK_REPLY_RECENT_CAP
      // turns. If the pool is too small to dedupe (all recently seen),
      // fall through and take the first 3 anyway.
      const fresh = pool.filter((q) => !seen.has(q.id))
      const chosen = (fresh.length >= 3 ? fresh : pool).slice(0, 3)
      if (chosen.length === 0) return fallback

      // Update the ring-buffer with what we're about to show.
      const nextRecent = [
        ...chosen.map((q) => q.id),
        ...quickReplyRecentIdsRef.current,
      ].slice(0, QUICK_REPLY_RECENT_CAP)
      quickReplyRecentIdsRef.current = nextRecent

      return chosen
    } catch (err) {
      console.warn('[quick-replies] gallery fetch failed, falling back:', err)
      return fallback
    }
  }

  const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36)

  const addMessage = useCallback((messageData: Omit<Message, 'id' | 'timestamp'>) => {
    const message: Message = {
      ...messageData,
      id: generateId(),
      timestamp: new Date()
    }
    setMessages(prev => [...prev, message])
    return message
  }, [])

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return

    // Add user message
    const userMessage = addMessage({
      type: 'user',
      content: content.trim(),
      status: 'sending'
    })

    // Clear quick replies
    setQuickReplies([])
    setIsTyping(true)
    setIsStreaming(false)
    streamingMessageIdRef.current = null

    // Update status
    setTimeout(() => {
      updateMessageStatus(userMessage.id, 'sent')
    }, 300)

    // Y3 2026-04-19: pipe the active sidebar event_id into chat context.
    // Without this the backend YoY resolver has no seed event to fall
    // back to when the message's tokens don't describe a specific event
    // ("Compare year over year" / "compare to last year" / comparator-
    // shaped phrasings). currentEventId is null on Settings or pre-event
    // routes — backend treats null as "no seed" and prompts the user.
    const sidebarEventId = typeof window !== 'undefined'
      ? Number(window.sessionStorage.getItem('butler-buddy:currentEventId') || '') || null
      : null

    // Send via WebSocket if connected (with streaming enabled)
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        sessionId: sessionIdRef.current,
        message: content.trim(),
        stream: true,
        context: {
          role: currentUser?.role || 'admin',
          eventStage: 'live_event',
          event_id: sidebarEventId,
          page: pageContextRef.current.page,
          route: pageContextRef.current.route,
          page_section: pageContextRef.current.section,
          page_description: pageContextRef.current.description,
          page_event_id: pageContextRef.current.eventId,
        }
      }))
    } else {
      // HTTP fallback (non-streaming)
      try {
        const response = await fetch(`${API_URL}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: sessionIdRef.current,
            message: content.trim(),
            context: {
              role: currentUser?.role || 'admin',
              eventStage: 'live_event',
              event_id: sidebarEventId
            }
          })
        })

        const data = await response.json()
        handleServerMessage({ type: 'response', ...data })
      } catch (error) {
        console.error('API error:', error)
        handleServerMessage({ type: 'error' })
      }
    }
  }, [addMessage, currentUser, handleServerMessage])

  const updateMessageStatus = useCallback((messageId: string, status: MessageStatus) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, status } : msg
    ))
  }, [])

  const requestEscalation = useCallback((reason?: string) => {
    setIsEscalated(true)
    addMessage({
      type: 'system',
      content: `Escalation requested${reason ? `: ${reason}` : ''}. The Butler approval path will notify MagicMoney and follow up shortly.`,
      metadata: { escalated: true, priority: 'high' }
    })
  }, [addMessage])

  const requestHandoff = useCallback((_reason?: string) => {
    setHandoffPending(true)
    addMessage({
      type: 'system',
      content: 'Connecting you to the Butler beta approval flow...',
      metadata: { handoffRequested: true, priority: 'medium' }
    })

    setTimeout(() => {
      setHandoffPending(false)
      setIsEscalated(true)
      addMessage({
        type: 'system',
        content: 'A support team member has been notified and will assist you shortly.',
        metadata: { escalated: true }
      })
    }, 2000)
  }, [addMessage])

  const setPendingMessage = useCallback((text: string) => {
    setPendingMessageState(text)
  }, [])

  // Ref to avoid stale closure in the timeout callback
  const pendingMessageRef = useRef<string | null>(null)
  pendingMessageRef.current = pendingMessage

  // Auto-send pending message when chat is active and connected.
  // If not connected yet, the effect re-runs when isConnected changes.
  // Timeout fallback: if still not connected after 5s, send via HTTP fallback.
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (!pendingMessage) return

    if (isConnected) {
      // Connected — send immediately
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current)
      sendMessage(pendingMessage)
      setPendingMessageState(null)
      return
    }

    // Not connected yet — set a timeout to send via HTTP fallback
    if (!pendingTimerRef.current) {
      pendingTimerRef.current = setTimeout(() => {
        // Read from ref to get current value, not stale closure
        const msg = pendingMessageRef.current
        if (msg) {
          sendMessage(msg)
          setPendingMessageState(null)
        }
        pendingTimerRef.current = undefined
      }, 5000)
    }

    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = undefined
      }
    }
  }, [pendingMessage, isConnected, sendMessage])

  const clearChat = useCallback(() => {
    setMessages([])
    setQuickReplies([])
    setIsEscalated(false)
    setHandoffPending(false)
    setIsStreaming(false)
    streamingMessageIdRef.current = null
    sessionIdRef.current = newChatSessionId()
  }, [])

  return (
    <ChatContext.Provider value={{
      messages,
      isTyping,
      isStreaming,
      isConnected,
      currentConversationId,
      quickReplies,
      isEscalated,
      handoffPending,
      pendingMessage,
      sendMessage,
      addMessage,
      setIsTyping,
      setIsConnected,
      setQuickReplies,
      setPendingMessage,
      requestEscalation,
      requestHandoff,
      clearChat,
      updateMessageStatus
    }}>
      {children}
    </ChatContext.Provider>
  )
}
